import { Controller, useFormContext } from "react-hook-form"
import { DualColumnRow, Subtitle } from "../../../../../../style"
import {
  OnChangePostalCodeParams,
  PostalCodeController,
} from "../../../../../PostalCodeController"
import { Trans, t } from "@lingui/macro"
import { useEffect, useMemo } from "react"

import { DropdownField } from "../../../../../../../../../../components/DropdownField"
import { ReceiverForm } from "../ReceiverForm"
import { ShippingMethodZData } from "../../../../types"
import { TextField } from "../../../../../../../../../../components/TextField"
import debounce from "lodash/debounce"
import { useCheckoutScreen } from "../../../../../../../../hooks/useCheckoutScreen"
import { useGeolocation } from "../../../../../../../../providers/GeoLocationProvider/GeolocationContext"

type Field = keyof ShippingMethodZData

export const ShippingMethodZ = () => {
  const { error } = useCheckoutScreen()
  const {
    errors,
    control,
    setError,
    setValue,
    watch,
    getValues,
  } = useFormContext<ShippingMethodZData>()
  useEffect(() => {
    const fields: Field[] = [
      "destinationAddressStreet",
      "destinationAddressNumber",
      "destinationCity",
      "destinationPostalCode",
      "destinationState",
    ]
    const errorMsg = {
      type: "manual",
      message: t`Revisá este dato y volvé a completarlo`,
    }

    if (error?.code === "quote_error")
      fields.forEach(field => setError(field, errorMsg))
    else if (error?.code === "postal_code")
      setError("destinationPostalCode", errorMsg)
  }, [error, setError])

  const {
    data,

    loading,
    fetchGeolocation,
    resetGeolocation,
    errorLocation,
  } = useGeolocation()

  const postalCode = watch("destinationPostalCode")

  const debouncedFetchGeolocation = useMemo(
    () =>
      debounce(code => {
        fetchGeolocation(code)
      }, 500),
    [fetchGeolocation]
  )

  const stateOptions = useMemo(
    () =>
      postalCode && data?.objects.states
        ? data.objects.states.map(state => ({
            id: state.stateId,
            name: state.name,
            value: state.name,
          }))
        : [],
    [postalCode, data?.objects.states]
  )

  const cityOptions = useMemo(
    () =>
      postalCode && data?.objects.cities
        ? data.objects.cities.map((city, i) => ({
            id: i.toString(),
            name: city.name,
            value: city.name,
          }))
        : [],
    [postalCode, data?.objects.cities]
  )

  useEffect(() => {
    setValue("destinationState", stateOptions[0]?.value)
    if (!postalCode) {
      setValue("destinationState", "")
    }
  }, [setValue, stateOptions, postalCode])

  useEffect(() => {
    setValue("destinationCity", cityOptions[0]?.value)
    if (!postalCode) {
      setValue("destinationCity", "")
    }
  }, [setValue, cityOptions, postalCode])

  useEffect(() => {
    if (errorLocation) {
      setValue("destinationState", "")
      setValue("destinationCity", "")
    }
  }, [setValue, errorLocation])

  useEffect(() => {
    return () => {
      resetGeolocation()
    }
  }, [resetGeolocation])

  const handlePostalCodeChange = ({
    value,
    postalCodeLenght,
  }: OnChangePostalCodeParams) => {
    const minLength = postalCodeLenght

    if (value.length >= minLength) {
      debouncedFetchGeolocation(value)
    } else if (data !== null) {
      resetGeolocation()
    }
  }

  return (
    <>
      <ReceiverForm title={t`¿Quién recibe?`} />
      <Subtitle>
        <Trans>¿Dónde la enviamos?</Trans>
      </Subtitle>

      <Controller
        control={control}
        name="destinationAddressStreet"
        defaultValue=""
        rules={{
          required: t`Completá el campo calle.`,
        }}
        render={({ name, value, onChange, onBlur }) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            label={<Trans>Calle (sin número)</Trans>}
            errorMsg={errors.destinationAddressStreet?.message}
            maxLength={100}
            hideLength={true}
          />
        )}
      />

      <DualColumnRow>
        <Controller
          control={control}
          name="destinationAddressNumber"
          defaultValue=""
          rules={{
            required: t`Completá el campo número.`,
          }}
          render={({ onChange, value, name }) => (
            <TextField
              name={name}
              label={<Trans>Número</Trans>}
              errorMsg={errors.destinationAddressNumber?.message}
              maxLength={100}
              hideLength={true}
              onChange={onChange}
              value={value?.replace(/[^0-9]/, "")}
            />
          )}
        />
        <Controller
          control={control}
          name="destinationAddressOther"
          defaultValue=""
          render={({ name, value, onChange, onBlur }) => (
            <TextField
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={<Trans>Departamento (opcional)</Trans>}
              maxLength={100}
              hideLength={true}
            />
          )}
        />
      </DualColumnRow>

      <DualColumnRow>
        <PostalCodeController
          name="destinationPostalCode"
          errorMsg={errorLocation || errors.destinationPostalCode?.message}
          control={control}
          onChangePostalCode={handlePostalCodeChange}
          hasLocationError={!!errorLocation}
          disabled={loading}
          defaultValue={getValues("destinationPostalCode")}
        />
      </DualColumnRow>

      <Controller
        control={control}
        name="destinationCity"
        defaultValue=""
        rules={{
          required: true,
        }}
        render={({ name, value, onChange }) => (
          <DropdownField
            name={name}
            value={value}
            onChange={onChange}
            label={<Trans>Localidad</Trans>}
            errorMsg={errors.destinationCity?.message}
            dataTestId={"shipping-method-city-dropdown"}
            options={cityOptions}
            disabled={cityOptions.length < 2}
            loading={loading}
          />
        )}
      />

      <Controller
        control={control}
        name="destinationState"
        rules={{
          required: true,
        }}
        defaultValue={
          stateOptions && stateOptions.length > 0 ? stateOptions[0].value : ""
        }
        render={({ onChange, value, name }) => (
          <DropdownField
            name={name}
            label={<Trans>Provincia</Trans>}
            options={stateOptions}
            disabled={stateOptions.length < 2}
            value={value}
            dataTestId={"shipping-method-state-dropdown"}
            loading={loading}
            onChange={selectedStateName => {
              onChange(selectedStateName)

              const selectedState = stateOptions.find(
                state => state.name === selectedStateName
              )

              if (postalCode && selectedState && stateOptions.length > 1) {
                fetchGeolocation(postalCode, selectedState.id)
              }
            }}
            errorMsg={errors.destinationState?.message}
          />
        )}
      />
    </>
  )
}
